import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import CloseIcon from "@mui/icons-material/Close";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "react-router-dom";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { useFormik } from "formik";
import * as yup from "yup";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import { useLoginMutation, useGetPublicSchoolInfoQuery } from "../../../app/authApiSlice";
import { useAppDispatch } from "../../../app/store";
import { login, selectCurrentToken, setUser } from "./authSlice";
import { apiSlice } from "../../../app/apiSlice";
import { useGetSchoolProfileQuery } from "../settings/school/schoolApiSlice";
import { useSelector } from "react-redux";
import { selectCurrentColor, setColor } from "../../../theme/themeSlice";
import { getApiBaseUrl } from "../../../helpers/apiUrl";

function Login() {
  const token = useSelector(selectCurrentToken);
  const currentThemeColor = useSelector(selectCurrentColor);
  const [loginMutation, { isLoading, error }] = useLoginMutation();
  const { data: publicSchoolInfo } = useGetPublicSchoolInfoQuery();
  const [showQr, setShowQr] = useState(false);

  const { data: schoolProfile, isLoading: isGettingSchoolProfile } =
    useGetSchoolProfileQuery(undefined, { skip: !token });

  const dispatch = useAppDispatch();

  const preferredColor = currentThemeColor || "#2574d1";

  // Build the QR payload encoding the current school's API URL
  const qrPayload = JSON.stringify({
    type: "schoola-add",
    url: getApiBaseUrl(),
  });

  const formik = useFormik({
    initialValues: { username: "", password: "" },
    validationSchema: yup.object({
      username: yup.string().required("Required"),
      password: yup.string().required("Required"),
    }),
    onSubmit: async (values) => {
      try {
        const userData = await loginMutation(values).unwrap();
        const fallbackSchoolId =
          userData.user?.roles?.find((role) => role?.school_id)?.school_id || null;
        dispatch(apiSlice.util.resetApiState());
        dispatch(
          login({
            ...userData,
            promptEmailUpdate: !!userData.prompt_email_update,
            user: { ...userData.user, current_school_id: fallbackSchoolId },
          })
        );
      } catch (err) {
      }
    },
  });

  // Once school profile loads, patch color and school id in the background
  useEffect(() => {
    if (!token || isGettingSchoolProfile || !schoolProfile) return;
    dispatch(setUser({ current_school_id: schoolProfile.id }));
    if (schoolProfile.color) {
      dispatch(setColor({ color: schoolProfile.color }));
    }
  }, [dispatch, token, isGettingSchoolProfile, schoolProfile]);

  return (
    <>
    <Box
      sx={{
        minHeight: "100vh",
        background: "#eee",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          backgroundImage: publicSchoolInfo?.logo ? `url(${publicSchoolInfo.logo})` : "none",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: { xs: "65vw", sm: "45vw", md: "32vw" },
          opacity: 0.08,
          filter: "grayscale(100%)",
          pointerEvents: "none",
        },
        "& .paperBody": {
          p: { xs: 2, sm: 4 },
        },

        "& form": {
          "& > *": {
            mb: 2.5,
          },
          "& .heading": {
            mt: 2,
            color: preferredColor,
            fontWeight: "bolder",
            fontVariant: "smallCaps",
            fontFamily:
              "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif",
            fontSize: "2.5rem",
          },
          "& .subHeading": {
            color: "#9faecb",
          },

          "& button": {
            background: preferredColor,
            "&:hover": {
              background: preferredColor,
            },
          },
        },
      }}
    >
      <Stack
        alignItems="center"
        spacing={2}
        sx={{ width: "100%", px: { xs: 2, sm: 0 }, position: "relative", zIndex: 1 }}
      >
        {publicSchoolInfo?.logo && (
          <Avatar
            alt={publicSchoolInfo.name || "School Logo"}
            src={publicSchoolInfo.logo}
            sx={{ width: { xs: "80px", sm: "124px" }, height: { xs: "80px", sm: "124px" } }}
          />
        )}
        <StyledPaper sx={{ width: { xs: "100%", sm: "480px", md: "520px" } }}>
          <Box className="paperBody">
            <Alert icon={false} severity="success">
              Please login to access this page
            </Alert>
            <Box component="form" onSubmit={formik.handleSubmit}>
            <Typography variant="h1" className="heading">
              LOGIN
            </Typography>
            <Typography className="subHeading">Sign in to your account</Typography>
            <FormControl error fullWidth>
              <StyledTextField
                autoFocus
                variant="outlined"
                id="username"
                name="username"
                placeholder="Username or Email"
                value={formik.values.username}
                onChange={formik.handleChange}
                error={formik.touched.username && Boolean(formik.errors.username)}
              />
              <FormHelperText variant="error">
                {formik.touched.username && formik.errors.username}
              </FormHelperText>
            </FormControl>
            <FormControl error fullWidth>
              <StyledTextField
                autoComplete="false"
                type="password"
                variant="outlined"
                id="password"
                name="password"
                placeholder="Password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
              />
              <FormHelperText variant="error">
                {formik.touched.password && formik.errors.password}
              </FormHelperText>
              {error && (
                <FormHelperText variant="error">
                  {error?.data?.message}
                </FormHelperText>
              )}
              <Typography variant="body2" sx={{ textAlign: "right", mt: 0.5 }}>
                <Link to="/forgot-password" style={{ color: preferredColor }}>
                  Forgot password?
                </Link>
              </Typography>
            </FormControl>
            <Stack direction="row">
              {isLoading ? (
                <CircularProgress />
              ) : (
                <Button type="submit" variant="contained">
                  Login
                </Button>
              )}
            </Stack>
          </Box>
          </Box>
          {/* Mobile app QR code section */}
          <Divider sx={{ my: 2 }} />
          <Stack alignItems="center" spacing={1}>
            <Tooltip title="Add this school to the Schoola mobile app">
              <Button
                variant="text"
                size="small"
                startIcon={<PhoneIphoneIcon />}
                onClick={() => setShowQr(true)}
                sx={{ color: preferredColor, textTransform: "none", fontSize: "0.85rem" }}
              >
                Add to Schoola mobile app
              </Button>
            </Tooltip>
          </Stack>
        </StyledPaper>
      </Stack>
    </Box>

    {/* QR Dialog */}
    <Dialog open={showQr} onClose={() => setShowQr(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>Add School to Mobile App</span>
        <IconButton size="small" onClick={() => setShowQr(false)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack alignItems="center" spacing={2} sx={{ py: 1 }}>
          {publicSchoolInfo?.logo && (
            <Avatar src={publicSchoolInfo.logo} alt={publicSchoolInfo.name} sx={{ width: 48, height: 48 }} />
          )}
          {publicSchoolInfo?.name && (
            <Typography variant="subtitle1" fontWeight="bold">{publicSchoolInfo.name}</Typography>
          )}
          <Box sx={{ p: 2, border: "1px solid #eee", borderRadius: 2, backgroundColor: "#fff" }}>
            <QRCodeSVG value={qrPayload} size={200} />
          </Box>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Open the <strong>Schoola</strong> app, tap <strong>Add a School</strong>, then scan this QR code.
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  </>
  );
}

export default Login;
